import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import {
  createBoard,
  type CreateBoardRequest,
  type CreatedBoard,
} from '@/features/class/boardsApi';
import { useAuthStore, type RawAuthUser } from '@/store/authStore';

/**
 * Push a freshly-created board into the auth-store's `rawUser` payload
 * so the Sidebar/profile (which read owned classes from the login
 * response, not React Query) re-render immediately without needing a
 * full page refresh.
 *
 * We tag the new entry with `role: "owner"` so `useOwnedBoards` picks it
 * up. The shape mirrors what `extractAllBoards` expects: a flat
 * `board[]` entry — and, if the user has the newer
 * `boardWithCategory[]` shape, we append the row to the first category
 * group (creating a synthetic "My classes" group as a last resort).
 */
function injectCreatedBoardIntoRawUser(created: CreatedBoard): void {
  const { rawUser, setRawUser } = useAuthStore.getState();
  if (!created || !created.id) return;

  const base: RawAuthUser = rawUser ? { ...rawUser } : {};
  const entry = {
    id: created.id,
    code: created.code ?? String(created.id),
    name: created.name,
    role: 'owner' as const,
    // Big position so it sorts to the end until the next login refresh.
    position: Number.MAX_SAFE_INTEGER - 1,
  };

  // 1) Maintain the modern `boardWithCategory[]` shape if present.
  const categorized = Array.isArray(base.boardWithCategory)
    ? [...(base.boardWithCategory as unknown[])]
    : null;
  if (categorized && categorized.length > 0) {
    const firstGroup = categorized[0] as Record<string, unknown> | undefined;
    if (firstGroup && Array.isArray(firstGroup.boards)) {
      categorized[0] = {
        ...firstGroup,
        boards: [...(firstGroup.boards as unknown[]), entry],
      };
      base.boardWithCategory = categorized;
    }
  }

  // 2) Always maintain the legacy flat `board[]` so older readers stay
  //    consistent. (Both arrays are deduped by id.)
  const flat = Array.isArray(base.board) ? [...(base.board as unknown[])] : [];
  const alreadyPresent = flat.some(
    (b) => b && typeof b === 'object' && (b as { id?: number | string }).id === entry.id,
  );
  if (!alreadyPresent) flat.push(entry);
  base.board = flat;

  setRawUser(base);
}

/**
 * POST /midl/api/v3/board — creates a new class (board).
 *
 * Wired from `ClassFormModal` so the "Create class" submit persists to
 * the real backend. On success we also patch the auth-store `rawUser`
 * so the sidebar/profile (login-payload driven) and the React-Query
 * joined-boards / class-detail caches all reflect the new class
 * without a hard reload.
 */
export function useCreateBoardMutation() {
  const queryClient = useQueryClient();

  return useMutation<CreatedBoard | null, unknown, CreateBoardRequest>({
    mutationKey: ['class', 'board', 'create'],
    mutationFn: (vars) => createBoard(vars),
    onSuccess: (created) => {
      if (created) {
        injectCreatedBoardIntoRawUser(created);
      }
      // Refresh server-driven sidebar/class lists too.
      void queryClient.invalidateQueries({ queryKey: ['boards', 'joined'] });
      if (created?.code) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.class.detail(created.code),
        });
      }
      // Also refresh the create-post audience picker cache so the composer
      // sees the freshly-created class without a full page reload. The
      // composer key is namespaced by the current user id; try the
      // authenticated user id first and fall back to the rawUser payload.
      try {
        const authState = useAuthStore.getState();
        const userId = authState.user?.id ?? (authState.rawUser?.id as string | undefined);
        if (userId) {
          void queryClient.invalidateQueries({ queryKey: queryKeys.createPost.classData(userId) });
        }
      } catch (err) {
        // Swallow — invalidation is best-effort and must not break the
        // mutation success path.
      }
    },
  });
}
